class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function formatMoney(amount, locale, currency) {
  const loc = locale || 'en-US';
  const curr = currency || 'USD';

  return new Intl.NumberFormat(loc, {
    style: 'currency',
    currency: curr,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount / 100);
}

if (!customElements.get('free-shipping')) {
  class FreeShipping extends HTMLElement {
    constructor() {
      super();
      this.locale = 'en-US';
      this.currency = 'USD';
    }

    connectedCallback() {
      let total = parseInt(this.dataset.cartTotal, 10);
      let showShipping = this.dataset.showShipping === 'true';

      this.locale = this.dataset.locale;
      this.currency = this.dataset.currency;

      let shippingMinimum = Math.round(parseInt(this.dataset.minimum, 10) * (Shopify.currency.rate || 1));

      let percentage = (shippingMinimum > 0) ? Math.min(total / shippingMinimum, 1) : 1;
      this.style.setProperty('--percentage', percentage);

      this.updateIcons(shippingMinimum, total);

      if (total < shippingMinimum) {
        this.showShippingProgress(total, shippingMinimum);
      } else {
        this.showShippingSuccess();
      }
    }

    showShippingProgress(total, threshold) {
      this.hideAllTexts();
      const remainingText = this.querySelector('.cart-drawer__progress-text-remaining--shipping');
      const amountSpan = remainingText ? remainingText.querySelector('strong') : null;

      if (remainingText) {
        remainingText.classList.remove('cart-drawer__hidden');
        remainingText.style.display = 'block';
      }

      let diff = threshold - total;
      if (amountSpan) amountSpan.innerHTML = formatMoney(diff, this.locale, this.currency);
    }

    showShippingSuccess() {
      this.hideAllTexts();
      const fullText = this.querySelector('.cart-drawer__progress-text-full--shipping');
      if (fullText) {
        fullText.classList.remove('cart-drawer__hidden');
        fullText.style.display = 'flex';
      }
    }

    hideAllTexts() {
      const texts = this.querySelectorAll('.cart-drawer__progress-text-remaining, .cart-drawer__progress-text-full');
      texts.forEach(text => {
        text.classList.add('cart-drawer__hidden');
        text.style.display = 'none';
      });
    }

    updateIcons(shippingMinimum, total) {
      const shippingIcon = this.querySelector('.cart-drawer__progress-milestone--shipping');
      if (shippingIcon) {
        shippingIcon.classList.remove('cart-drawer__hidden');
        shippingIcon.style.left = '100%';

        if (total >= shippingMinimum) shippingIcon.classList.add('achieved');
        else shippingIcon.classList.remove('achieved');
      }
    }
  }
  customElements.define('free-shipping', FreeShipping);
}
